/**
 * Product Image Carousel
 *
 * Custom element that handles:
 * - Carousel mode with main image + thumbnail navigation
 * - Column/grid mode showing all images stacked
 * - Active thumbnail border state
 * - Click to zoom (opens image in overlay)
 * - Swipe/drag support on mobile
 * - Keyboard navigation
 */
class ProductImageCarousel extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.animating = false;
  }

  connectedCallback() {
    this.mainImage = this.querySelector('[data-main-image]');
    this.mainImageContainer = this.querySelector('[data-main-image-container]');
    this.thumbnails = this.querySelectorAll('[data-thumbnail]');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.slides = this.querySelectorAll('[data-slide]');
    this.slideTrack = this.querySelector('[data-slide-track]');
    this.zoomOverlay = this.querySelector('[data-zoom-overlay]');
    this.zoomImage = this.querySelector('[data-zoom-image]');
    this.zoomClose = this.querySelector('[data-zoom-close]');
    this.counter = this.querySelector('[data-counter-current]');
    this.totalCounter = this.querySelector('[data-counter-total]');

    this.totalSlides = this.slides.length;

    if (this.totalSlides === 0) return;

    this.bindEvents();
    this.updateState();
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  bindEvents() {
    // Thumbnail clicks
    this.thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.goToSlide(index));
    });

    // Arrow navigation
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev());
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
    }

    // Touch/drag events on slide track
    if (this.slideTrack) {
      this.slideTrack.addEventListener('mousedown', this.onDragStart.bind(this));
      this.slideTrack.addEventListener('touchstart', this.onDragStart.bind(this), {
        passive: true,
      });
      document.addEventListener('mousemove', this.onDragMove.bind(this));
      document.addEventListener('touchmove', this.onDragMove.bind(this), { passive: false });
      document.addEventListener('mouseup', this.onDragEnd.bind(this));
      document.addEventListener('touchend', this.onDragEnd.bind(this));
    }

    // Keyboard navigation
    this.addEventListener('keydown', this.onKeyDown.bind(this));

    const zoomTrigger = this.querySelector('[data-zoom-trigger]');
    if (zoomTrigger) {
      zoomTrigger.addEventListener('click', () => {
        this.openZoom(this.currentIndex); // zoom current slide
      });
    }

    if (this.zoomClose) {
      this.zoomClose.addEventListener('click', () => this.closeZoom());
    }

    if (this.zoomOverlay) {
      this.zoomOverlay.addEventListener('click', (e) => {
        // Close when clicking the dialog backdrop or the image itself
        if (e.target === this.zoomOverlay || e.target === this.zoomImage) {
          this.closeZoom();
        }
      });
      // Handle native dialog cancel event (e.g. Escape key)
      this.zoomOverlay.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.closeZoom();
      });
    }

    // Escape key closes zoom
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeZoom();
    });
  }

  unbindEvents() {
    document.removeEventListener('mousemove', this.onDragMove);
    document.removeEventListener('mouseup', this.onDragEnd);
    document.removeEventListener('touchmove', this.onDragMove);
    document.removeEventListener('touchend', this.onDragEnd);
  }

  onDragStart(e) {
    if (this.animating) return;

    // Prevent default browser image dragging
    if (e.type === 'mousedown') {
      e.preventDefault();
    }

    this.isDragging = true;
    this.startX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
    this.currentX = this.startX;

    if (this.slideTrack) {
      this.slideTrack.style.transition = 'none';
    }
  }

  onDragEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;

    const diff = this.currentX - this.startX;
    // Ensure the track returns to a clean snap point
    if (this.slideTrack) {
      this.slideTrack.style.transition = 'transform 0.3s ease';
    }

    const threshold = this.slideTrack ? this.slideTrack.offsetWidth * 0.15 : 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0 && this.currentIndex > 0) {
        this.prev();
      } else if (diff < 0 && this.currentIndex < this.totalSlides - 1) {
        this.next();
      } else {
        this.goToSlide(this.currentIndex);
      }
    } else {
      this.goToSlide(this.currentIndex);
    }
  }

  onDragMove(e) {
    if (!this.isDragging) return;

    this.currentX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
    const diff = this.currentX - this.startX;

    if (this.slideTrack) {
      const offset = -(this.currentIndex * 100) + (diff / this.slideTrack.offsetWidth) * 100;
      this.slideTrack.style.transform = `translateX(${offset}%)`;
    }

    if (e.type === 'touchmove' && Math.abs(diff) > 10) {
      e.preventDefault();
    }
  }

  onKeyDown(e) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.prev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.next();
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.goToSlide(this.currentIndex - 1);
    }
  }

  next() {
    if (this.currentIndex < this.totalSlides - 1) {
      this.goToSlide(this.currentIndex + 1);
    }
  }

  goToSlide(index) {
    if (index < 0 || index >= this.totalSlides) return;

    this.currentIndex = index;
    this.updateState();
  }

  updateState() {
    // Update slide track position
    if (this.slideTrack) {
      this.slideTrack.style.transform = `translateX(-${this.currentIndex * 100}%)`;
    }

    // Update active thumbnail
    this.thumbnails.forEach((thumb, i) => {
      const isActive = i === this.currentIndex;
      thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
      thumb.classList.toggle('border-gray-900', isActive);
      thumb.classList.toggle('border-transparent', !isActive);
    });

    // Update slide visibility
    this.slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i !== this.currentIndex ? 'true' : 'false');
    });

    // Update arrow states
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex === 0;
      this.prevBtn.classList.toggle('opacity-30', this.currentIndex === 0);
      this.prevBtn.classList.toggle('cursor-not-allowed', this.currentIndex === 0);
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex === this.totalSlides - 1;
      this.nextBtn.classList.toggle('opacity-30', this.currentIndex === this.totalSlides - 1);
      this.nextBtn.classList.toggle(
        'cursor-not-allowed',
        this.currentIndex === this.totalSlides - 1
      );
    }

    // Update counter
    if (this.counter) {
      this.counter.textContent = this.currentIndex + 1;
    }

    // Scroll active thumbnail into view
    const activeThumbnail = this.thumbnails[this.currentIndex];
    if (activeThumbnail) {
      activeThumbnail.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }

  openZoom(index) {
    if (!this.zoomOverlay) return;

    const slide = this.slides[index];
    if (!slide) return;

    const img = slide.querySelector('img');
    if (!img) return;

    // Use the highest resolution available
    const highResUrl = img.dataset.maxResolution || img.src;

    if (this.zoomImage) {
      this.zoomImage.src = highResUrl;
      this.zoomImage.alt = img.alt || '';
    }

    this.zoomOverlay.showModal();
    document.body.style.overflow = 'hidden';
  }

  closeZoom() {
    if (!this.zoomOverlay || !this.zoomOverlay.open) return;

    this.zoomOverlay.close();
    document.body.style.overflow = '';
  }
}

if (!customElements.get('product-image-carousel')) {
  customElements.define('product-image-carousel', ProductImageCarousel);
}
